/**
 * effects.ts — Premium UI interaction effects system
 *
 * Provides composable CSS class helpers and constants for the
 * new-normal.ai interaction design language. Every interactive
 * element in the app should use one of these effect presets
 * to ensure consistent, compelling hover/active/click feedback.
 *
 * Effect classes reference keyframes and utility classes defined
 * in index.css under "Premium interaction effects".
 *
 * Usage:
 *   import { fx } from "@/lib/effects";
 *   <button className={cn("...", fx.toolbar)}>
 *   <div className={cn("...", fx.card)}>
 */

// ─── Composable effect class strings ─────────────────────────────────────────

/** Toolbar icon buttons — subtle lift + icon nudge on hover */
export const FX_TOOLBAR =
  "fx-press fx-icon-nudge transition-all duration-150";

/** Primary CTA buttons — glow + shimmer sweep */
export const FX_CTA =
  "fx-glow fx-shimmer transition-all duration-200";

/** Card containers — 3D lift on hover */
export const FX_CARD =
  "fx-lift transition-all duration-200";

/** Floating action buttons (Sage trigger) — magnetic hover */
export const FX_FLOAT =
  "fx-magnetic transition-all duration-200";

/** Ghost/subtle buttons — just press depth */
export const FX_GHOST =
  "fx-press transition-all duration-150";

/** Nav items — subtle lift + glow on active */
export const FX_NAV =
  "fx-press transition-all duration-150";

/** Tab/filter pill buttons — press + subtle shadow */
export const FX_PILL =
  "fx-press transition-all duration-150";

// ─── Convenience namespace ───────────────────────────────────────────────────

export const fx = {
  toolbar: FX_TOOLBAR,
  cta: FX_CTA,
  card: FX_CARD,
  float: FX_FLOAT,
  ghost: FX_GHOST,
  nav: FX_NAV,
  pill: FX_PILL,
} as const;

// ─── Helper: camera export filename ──────────────────────────────────────────

/** Generate a timestamped filename for radar 3D PNG export */
export function radarExportFilename(): string {
  const now = new Date();
  const y = now.getFullYear();
  const ts = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `new-normal-radar3d-${y}-${ts}.png`;
}

// ─── Helper: save canvas as PNG ──────────────────────────────────────────────

/**
 * Export a canvas element as a PNG file.
 * Uses showSaveFilePicker (Chrome) with anchor download fallback.
 */
export async function saveCanvasAsPng(
  canvas: HTMLCanvasElement,
  filename: string
): Promise<void> {
  // Try modern File System Access API (Chrome/Edge)
  if ("showSaveFilePicker" in window) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handle = await (window as Record<string, any>).showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: "PNG Image",
            accept: { "image/png": [".png"] },
          },
        ],
      });
      const writable = await handle.createWritable();
      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), "image/png")
      );
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err: unknown) {
      // User cancelled — don't fall through to anchor download
      if (err instanceof DOMException && err.name === "AbortError") return;
    }
  }

  // Fallback: anchor download (Safari, Firefox, older browsers)
  const dataUrl = canvas.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
